import { createSlice } from "@reduxjs/toolkit";

const initialState = {
    cartItems: [], // Main cart items array
    count: 0,
    totalAmount: 0,
    isLoggedIn: false,
    userInfo: null
}

const cartSlice = createSlice({
    name: "hejal",
    initialState,
    reducers: {
        // Set entire cart from API response
        setCart: (state, action) => {
            const cartData = action.payload;
            
            if (cartData && Array.isArray(cartData)) {
                // Handle direct array of cart items
                state.cartItems = cartData.map(item => ({
                    _id: item._id,
                    product: item.product,
                    productId: item.product?._id,
                    quantity: item.quantity,
                    variantSku: item.variantSku,
                    selectedOptions: item.selectedOptions,
                    pricing: item.pricing,
                    variant: item.variant,
                    mainImage: item.selectedOptions?.color?.image || item.variant?.color?.images?.[0] || ''
                }));
            } else if (cartData && cartData.items && Array.isArray(cartData.items)) {
                // Handle cart response with items property
                state.cartItems = cartData.items.map(item => ({
                    _id: item._id,
                    product: item.product,
                    productId: item.product?._id,
                    quantity: item.quantity,
                    variantSku: item.variantSku,
                    selectedOptions: item.selectedOptions,
                    pricing: item.pricing,
                    variant: item.variant,
                    mainImage: item.selectedOptions?.color?.image || item.variant?.color?.images?.[0] || ''
                }));
                
                // Set totals if available
                if (cartData.totals) {
                    state.count = cartData.totals.totalQuantity || 0;
                    state.totalAmount = cartData.totals.totalAmount || 0;
                }
            } else {
                state.cartItems = [];
                state.count = 0;
                state.totalAmount = 0;
            }
            
            // Recalculate count if not provided
            if (!cartData?.totals?.totalQuantity) {
                state.count = state.cartItems.reduce((total, item) => total + (item.quantity || 0), 0);
            }
        },

        // Add single item to cart
        addItemToCart: (state, action) => {
            const newItem = action.payload;
            
            // Check if item already exists
            const existingItemIndex = state.cartItems.findIndex(item => 
                item._id === newItem._id || 
                (item.productId === newItem.productId && item.variantSku === newItem.variantSku)
            );

            if (existingItemIndex !== -1) {
                // Update existing item
                state.cartItems[existingItemIndex] = {
                    ...state.cartItems[existingItemIndex],
                    ...newItem,
                    quantity: newItem.quantity || state.cartItems[existingItemIndex].quantity
                };
            } else {
                // Add new item
                state.cartItems.push({
                    _id: newItem._id,
                    product: newItem.product,
                    productId: newItem.product?._id || newItem.productId,
                    quantity: newItem.quantity || 1,
                    variantSku: newItem.variantSku,
                    selectedOptions: newItem.selectedOptions,
                    pricing: newItem.pricing,
                    variant: newItem.variant,
                    mainImage: newItem.selectedOptions?.color?.image || newItem.variant?.color?.images?.[0] || ''
                });
            }

            // Recalculate count
            state.count = state.cartItems.reduce((total, item) => total + (item.quantity || 0), 0);
        },

        // Remove item from cart
        removeItem: (state, action) => {
            const { cartItemId } = action.payload;
            
            state.cartItems = state.cartItems.filter(item => item._id !== cartItemId);
            
            // Recalculate count
            state.count = state.cartItems.reduce((total, item) => total + (item.quantity || 0), 0);
        },

        // Update item quantity
        updateItemQuantity: (state, action) => {
            const { cartItemId, newQuantity } = action.payload;
            
            const itemIndex = state.cartItems.findIndex(item => item._id === cartItemId);
            
            if (itemIndex !== -1) {
                if (newQuantity > 0) {
                    state.cartItems[itemIndex].quantity = newQuantity;
                } else {
                    // Remove item if quantity is 0
                    state.cartItems.splice(itemIndex, 1);
                }
                
                // Recalculate count
                state.count = state.cartItems.reduce((total, item) => total + (item.quantity || 0), 0);
            }
        },

        // Clear entire cart
        clearCart: (state) => {
            state.cartItems = [];
            state.count = 0;
            state.totalAmount = 0;
        },

        // Set loading state
        setCartLoading: (state, action) => {
            state.isLoading = action.payload;
        },

        // Set user info
        setUserInfo: (state, action) => {
            state.userInfo = action.payload;
            state.isLoggedIn = !!action.payload;
        },

        // Logout
        logout: (state) => {
            state.userInfo = null;
            state.isLoggedIn = false;
            state.cartItems = [];
            state.count = 0;
            state.totalAmount = 0;
        },

        // Legacy actions for backward compatibility
        addToCart: (state, action) => {
            // Redirect to addItemToCart
            cartSlice.caseReducers.addItemToCart(state, action);
        },

        deleteItem: (state, action) => {
            // Redirect to removeItem
            const { productId, cartId } = action.payload;
            cartSlice.caseReducers.removeItem(state, { 
                payload: { cartItemId: cartId || productId } 
            });
        },

        increaseQuantity: (state, action) => {
            const { _id } = action.payload;
            const item = state.cartItems.find(item => 
                item._id === _id || item.productId === _id
            );
            if (item) {
                cartSlice.caseReducers.updateItemQuantity(state, {
                    payload: { cartItemId: item._id, newQuantity: item.quantity + 1 }
                });
            }
        },

        decreaseQuantity: (state, action) => {
            const { _id } = action.payload;
            const item = state.cartItems.find(item => 
                item._id === _id || item.productId === _id
            );
            if (item) {
                cartSlice.caseReducers.updateItemQuantity(state, {
                    payload: { cartItemId: item._id, newQuantity: Math.max(0, item.quantity - 1) }
                });
            }
        }
    }
})

export const {
    setCart,
    addItemToCart,
    removeItem,
    updateItemQuantity,
    clearCart,
    setCartLoading,
    setUserInfo,
    logout,
    // Legacy exports
    addToCart,
    deleteItem,
    increaseQuantity,
    decreaseQuantity
} = cartSlice.actions

export default cartSlice.reducer